<script setup>
import { computed, reactive, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import { debounce } from '@chatwoot/utils';
import { useMapGetter } from 'dashboard/composables/store';
import countries from 'shared/constants/countries.js';
import CompanyAPI from 'dashboard/api/companies';
import {
  checkWhatsAppNumber,
  normalizeBrazilPhone,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  STATE: { field: 'additionalAttributes.state' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyId: null,
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    state: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

// Company search state
const companySearchResults = ref([]);
const currentCompanyOption = ref(null);
let companySearchTimeout = null;

const companyOptions = computed(() => {
  const results = companySearchResults.value.map(c => ({
    label: c.name + (c.domain ? ` (${c.domain})` : ''),
    value: c.id,
  }));
  // Include currently selected company so ComboBox can display it
  if (currentCompanyOption.value) {
    const exists = results.some(
      o => o.value === currentCompanyOption.value.value
    );
    if (!exists) {
      results.unshift(currentCompanyOption.value);
    }
  }
  // Add clear option when a company is selected
  if (state.companyId) {
    results.unshift({
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COMPANY_NAME.NONE'),
      value: '',
    });
  }
  return results;
});

const handleCompanySearch = query => {
  clearTimeout(companySearchTimeout);
  if (!query || query.length < 2) {
    companySearchResults.value = [];
    return;
  }
  companySearchTimeout = setTimeout(async () => {
    try {
      const { data } = await CompanyAPI.search(query, 1, 'name');
      companySearchResults.value = data.payload || [];
    } catch {
      companySearchResults.value = [];
    }
  }, 300);
};

const handleCompanySelection = value => {
  if (!value || value === '') {
    state.companyId = null;
    currentCompanyOption.value = null;
    state.additionalAttributes.companyName = '';
  } else {
    state.companyId = value;
    const selected = companySearchResults.value.find(c => c.id === value);
    if (selected) {
      currentCompanyOption.value = {
        label: selected.name + (selected.domain ? ` (${selected.domain})` : ''),
        value: selected.id,
      };
      state.additionalAttributes.companyName = selected.name;
    }
  }
  companySearchResults.value = [];
  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    company,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    state: stateValue = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    companyId: company?.id || null,
    additionalAttributes: {
      description,
      companyName: company?.name || companyName,
      countryCode,
      country,
      city,
      state: stateValue,
      socialProfiles,
    },
  });

  // Initialize company display for ComboBox
  if (company?.id) {
    currentCompanyOption.value = {
      label: company.name || '',
      value: company.id,
    };
  } else {
    currentCompanyOption.value = null;
  }
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
  currentCompanyOption.value = null;
  companySearchResults.value = [];
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// --- WhatsApp verification (debounced, inline badge) ---
const whatsappVerification = ref(null);
const inboxesGetter = useMapGetter('inboxes/getInboxes');
const accountIdGetter = useMapGetter('getCurrentAccountId');

const dinastiaInbox = computed(() =>
  inboxesGetter.value?.find(
    i =>
      i.channel_type === 'Channel::DinastiaWhatsapp' &&
      i.connection_status === 'connected'
  )
);

const runWhatsappCheck = debounce(async phone => {
  if (!dinastiaInbox.value) {
    whatsappVerification.value = null;
    return;
  }
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length < 8) {
    whatsappVerification.value = null;
    return;
  }
  whatsappVerification.value = { checking: true };
  try {
    const normalized = normalizeBrazilPhone(phone);
    const result = await checkWhatsAppNumber(
      normalized,
      accountIdGetter.value,
      dinastiaInbox.value.id
    );
    whatsappVerification.value = {
      checking: false,
      registered: result?.registered === true,
    };
  } catch {
    whatsappVerification.value = null;
  }
}, 600);

watch(
  () => state.phoneNumber,
  newValue => runWhatsappCheck(newValue)
);

onUnmounted(() => runWhatsappCheck.cancel());

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
  whatsappVerification,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <div v-else-if="item.key === 'PHONE_NUMBER'" class="flex flex-col">
            <PhoneNumberInput
              v-model="getFormBinding(item.key).value"
              :placeholder="item.placeholder"
              :show-border="isDetailsView"
            />
            <div
              v-if="whatsappVerification"
              class="mt-1 flex items-center gap-1 text-xs"
              :class="{
                'text-n-slate-10': whatsappVerification.checking,
                'text-green-700 dark:text-green-400':
                  whatsappVerification.registered === true,
                'text-amber-700 dark:text-amber-400':
                  whatsappVerification.registered === false,
              }"
            >
              <span
                v-if="whatsappVerification.checking"
                class="i-lucide-loader-2 animate-spin size-3"
              />
              <span
                v-else-if="whatsappVerification.registered === true"
                class="i-lucide-check-circle size-3"
              />
              <span v-else class="i-lucide-alert-triangle size-3" />
              <span>{{
                whatsappVerification.checking
                  ? t(
                      'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.WHATSAPP_CHECKING'
                    )
                  : whatsappVerification.registered
                    ? t(
                        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.WHATSAPP_VERIFIED'
                      )
                    : t(
                        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.WHATSAPP_NOT_REGISTERED'
                      )
              }}</span>
            </div>
          </div>
          <ComboBox
            v-else-if="item.key === 'COMPANY_NAME'"
            :model-value="state.companyId || ''"
            :options="companyOptions"
            :placeholder="item.placeholder"
            :empty-state="
              t(
                'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COMPANY_NAME.NO_RESULTS'
              )
            "
            use-api-results
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @search="handleCompanySearch"
            @update:model-value="handleCompanySelection"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
